import type React from "react";
import {
  composeRenderProps,
  Group,
  type GroupProps,
  Switch as RACSwitch,
  type SwitchProps as RACSwitchProps,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import {
  DescriptionProvider,
  LabeledGroup,
  useDescription,
} from "@/components/ui/field";
import { cn, composeTailwindRenderProps } from "@/components/ui/utils";

export function SwitchGroup(props: GroupProps) {
  return (
    <LabeledGroup>
      <Group
        {...props}
        className={composeTailwindRenderProps(
          props.className,
          "flex flex-col gap-2",
        )}
      />
    </LabeledGroup>
  );
}

export function SwitchField({
  className,
  ...props
}: React.JSX.IntrinsicElements["div"]) {
  return (
    <DescriptionProvider>
      <div
        {...props}
        data-ui="field"
        className={cn(
          "group flex flex-col gap-y-0.5",
          "[&_[slot=description]:not([class*=pe-])]:has-[label[data-label-placement=start]]:pe-[calc(theme(width.8)+16px)]",
          "[&_[slot=description]:not([class*=ps-])]:has-[label[data-label-placement=end]]:ps-[calc(theme(width.8)+12px)]",
          "[&_[slot=description]]:text-xs",
          "[&_label]:has-data-[ui=description]:font-medium",
          "data-[ui=description]:**:has-[label[data-disabled]]:opacity-50",
          className,
        )}
      />
    </DescriptionProvider>
  );
}

interface SwitchProps extends RACSwitchProps {
  labelPlacement?: "start" | "end";
  render?: never;
}

const switchStyles = tv({
  base: "group relative flex items-center text-sm",
  slots: {
    root: "peer inline-flex h-5 w-8 shrink-0 cursor-default items-center rounded-full border-2 border-transparent outline-offset-2 transition-colors",
    thumb:
      "pointer-events-none block size-4 rounded-full bg-background shadow-sm shadow-black/5 ring-0 transition-transform duration-300 [transition-timing-function:cubic-bezier(0.16,1,0.3,1)]",
  },
  variants: {
    labelPlacement: {
      start: { base: "flex-row-reverse justify-between", root: "ms-3" },
      end: { root: "me-3" },
    },
    isDisabled: {
      true: { base: "opacity-50", root: "opacity-50 cursor-not-allowed" },
    },
    isFocusVisible: {
      true: { root: "outline-2 outline-offset-2 outline-ring/70" },
    },
    isSelected: {
      true: {
        root: "bg-primary",
        thumb: "translate-x-3 rtl:-translate-x-3",
      },
      false: { root: "bg-input", thumb: "translate-x-0" },
    },
  },
});

const styles = switchStyles();

export function Switch(props: SwitchProps) {
  const descriptionContext = useDescription();

  const { labelPlacement = "end", children, ...restProps } = props;

  return (
    <RACSwitch
      {...restProps}
      aria-describedby={descriptionContext?.["aria-describedby"]}
      data-label-placement={labelPlacement}
      className={composeRenderProps(props.className, (className, renderProps) =>
        styles.base({ ...renderProps, className, labelPlacement }),
      )}
    >
      {(renderProps) => (
        <>
          <div className={styles.root({ ...renderProps, labelPlacement })}>
            <span data-ui="thumb" className={styles.thumb(renderProps)} />
          </div>
          {typeof children === "function" ? children(renderProps) : children}
        </>
      )}
    </RACSwitch>
  );
}
